import { useState } from 'react';

import GoogleGeocoder from '~/src/common/services/Google/GoogleGeocoder';
import I18n from '~/src/common/services/I18n';
import { logger } from '~/src/common/services/Logger';
import Toaster from '~/src/common/services/Toaster';
import Tracker from '~/src/common/services/Tracker';
import { noop } from '~/src/common/utils/function';
import { getOs } from '~/src/common/utils/navigator';

// Permet d'utiliser la geolocalisation de manière asynchrone en cas de succès
const getCurrentPosition = (
  onError: PositionErrorCallback,
): Promise<GeolocationPosition | null> => {
  return new Promise<GeolocationPosition>(resolve => {
    navigator.geolocation.getCurrentPosition(
      (position: GeolocationPosition) => resolve(position),
      error => {
        onError(error);
      },
    );
  });
};

interface Props {
  onSuccess: (results: google.maps.GeocoderResult[]) => void;
}

const useGeolocation = ({ onSuccess }: Props) => {
  const [isLoading, setLoading] = useState(false);

  const handleGeolocation = async () => {
    Tracker.sendEvent('click cta geolocation', { 'event source': 'navigation' });

    setLoading(true);

    try {
      const position = await getCurrentPosition(() => {
        const isMobile = ['iOS', 'Android'].includes(getOs());
        Toaster.notify({
          icon: 'geolocation',
          message: I18n.t(`google-autocomplete.enable-location-${isMobile ? 'mobile' : 'desktop'}`),
        });
        setLoading(false);
      }).catch(noop);

      if (!position) {
        throw new Error('getCurrentPosition - position is null (need to enable geolocation)');
      }

      const results = await GoogleGeocoder.geocode({
        location: { lat: position.coords.latitude, lng: position.coords.longitude },
      });

      onSuccess(results);
    } catch (err) {
      logger.warning('Google geocoding error', {
        originalError: err as Error,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    isLoading,
    handleGeolocation,
  };
};

export default useGeolocation;
